import { z } from 'zod';
/**
 * Bot Telegram — docs/plan-telegram.md.
 *
 * Một bot cho **cả bản cài đặt** (quyết định 1): cùng bản chất với
 * `oauth_config`, nên nó nằm sau `system_config:manage` chứ không phải sau một
 * quyền cấp tổ chức. Nhiều tổ chức trong một bản cài dùng chung `@ten_bot`, và
 * `telegram_link` mới là thứ gắn một người với một tài khoản Telegram.
 */
/**
 * Cách nhận update từ Telegram.
 *
 * `polling` là mặc định và đó là một quyết định, không phải một mặc định lười:
 * webhook đòi URL HTTPS công khai ở cổng 443/80/88/8443, tức là tính năng này
 * sẽ không chạy được ở giai đoạn local — và một tính năng chỉ chạy ở production
 * là một tính năng không ai kiểm được trước khi deploy.
 */
export const TelegramMode = z.enum(['polling', 'webhook']);
export const TelegramBotStatus = z.object({
    configured: z.boolean(),
    /** `null` khi chưa cấu hình — mọi trường dưới đây cũng vậy. */
    botUsername: z.string().nullable(),
    tokenLast4: z.string().nullable(),
    mode: TelegramMode.nullable(),
    verifiedAt: z.iso.datetime().nullable(),
    /**
     * Lỗi gần nhất của vòng nhận update, hoặc `null`.
     *
     * Có mặt vì kiểu hỏng nguy hiểm nhất của long polling là hỏng **im lặng**:
     * mạng công ty chặn `api.telegram.org` thì bot đơn giản là không trả lời ai
     * cả, và không màn hình nào nói ra lý do.
     */
    lastError: z.string().nullable(),
    /** Lượt nhận update gần nhất. `null` = chưa từng nhận được gì. */
    lastUpdateAt: z.iso.datetime().nullable(),
    /**
     * `APP_URL` có phải `https` không. `false` ⇒ Mini App và nút menu không đặt
     * được, và màn hình quản trị phải nói thẳng điều đó thay vì để
     * `setChatMenuButton` hỏng lặng lẽ.
     */
    appUrlIsHttps: z.boolean(),
    /**
     * Có bot đang bật nhưng nó thuộc **môi trường khác** — APM-TG-env. Giá trị là
     * origin của nơi đã lưu nó (vd `https://chillpm.net-solutions.vn`). Khi ấy
     * `configured = false`: môi trường này không poll, không gửi tin bằng bot ấy,
     * vì một bản sao DB poll cùng token là giành update với bản thật.
     */
    foreignOrigin: z.string().nullable(),
});
export const SaveTelegramBotInput = z.object({
    /**
     * Token từ @BotFather, dạng `<bot_id>:<35 ký tự>`.
     *
     * `.trim()` **trước** `.min()` — xem bẫy trong CLAUDE.md. Người dùng luôn dán
     * kèm khoảng trắng ở đây vì token đến từ một tin nhắn Telegram.
     */
    token: z
        .string()
        .trim()
        .min(20)
        .max(200)
        .regex(/^\d{5,}:[A-Za-z0-9_-]{30,}$/, 'Token không đúng dạng của @BotFather'),
    mode: TelegramMode.default('polling'),
});
// ── Liên kết tài khoản (APM-TG-d) ───────────────────────────────────────────
/**
 * Loại thông báo được đẩy sang Telegram.
 *
 * Trùng `titleKey` của `NotificationPersist`, bỏ tiền tố `notifications.`.
 * Trùng có chủ đích: một khoá duy nhất đi từ nơi phát thông báo tới ô công tắc
 * trong màn hình cài đặt, không có bảng ánh xạ nào ở giữa để lệch.
 */
export const TELEGRAM_NOTIFICATION_TYPES = [
    'task.assigned',
    'task.unassigned',
    'task.edited',
    // Việc đang làm còn ≤ 10% thời gian tới hạn — APM-TLOW (người dùng yêu cầu 2026-10-15).
    'task.time_running_out',
    'comment.mentioned',
    'announcement.sent',
    'membership_request.invited',
    // Có người đăng ký vào dự án qua link, chờ PM chốt — APM-ROLE-p2.
    'membership_request.applied',
    // Duyệt dự án — APM-ROLE-m3. Là loại SSE (`notification.type`), không phải
    // `titleKey`: một loại ở đây gửi được nhiều câu (duyệt / từ chối).
    'project_approval.requested',
    'project_approval.approved',
    'project_approval.rejected',
    'project_approval.decided_by_other',
    'project.member_left',
    'raid.risk_high',
    'raid.signals_detected',
    'schedule.drift_detected',
    'schedule.impact_detected',
    'schedule.deadline_changed',
    // Sprint hết hạn tự đóng — báo PM trước 2 ngày, sáng ngày cuối, và khi đã đóng (2026-10-13).
    'sprint.ending_soon',
    'sprint.last_day',
    'sprint.auto_closed',
    'handover.suggested',
    'design_file.remote_version_detected',
    'timesheet.timer_overdue',
    'timesheet.session_capped',
    'timesheet.overtime_capped',
    'timesheet.day_capped',
    // Đầu ca chưa bấm giờ, kèm nút xin nghỉ nhanh — APM-TREM-c (người dùng yêu cầu 2026-10-19).
    'timesheet.start_reminder',
    // Có người mình quản lý trực tiếp xin nghỉ / rút đơn — APM-TREM-e (người dùng yêu cầu 2026-10-22).
    'absence.requested',
    'absence.retracted',
    // Có người xin vào tổ chức qua link cần duyệt — báo quản trị tổ chức (APM-ORGJR-c, người dùng
    // yêu cầu 2026-10-22).
    'org_join_request.created',
    'ai_budget.soft_limit_reached',
    // Người gửi / người theo dõi issue — APM-ISX-c.
    'issue.state_changed',
    'issue.resolved',
    'issue.reopened',
    'issue.watcher_added',
    // Trao đổi trên issue và giao người xử lý — APM-ISC-c.
    'issue.replied',
    'issue.customer_replied',
    'issue.internal_comment',
    'issue.assigned',
    // Đề xuất gộp / tách tổ chức — APM-ORGMS-c. Một loại cho mọi câu (mời, bên kia nhận, cấu
    // hình đổi, từ chối, huỷ, hết hạn): người nhận là người duyệt, và câu nào cũng đòi họ xem lại.
    'org_restructure.updated',
    // Nhắc báo số KR tự do mỗi mốc % của kỳ — người dùng yêu cầu 2026-10-01.
    'okr.report_due',
    // Luồng lỗi kiểm thử — APM-TEST2-b: được giao, đã sửa xong, vẫn lỗi, đã đóng.
    'bug.assigned',
    'bug.fixed',
    'bug.still_failing',
    'bug.closed',
    // Mời chốt sổ phương án — APM-DSHEET-i. Bật sẵn (người dùng chốt 2026-10-07).
    'decision_sheet.invited',
    // Tài liệu được gửi duyệt — báo PM (người dùng yêu cầu 2026-10-13).
    'document.review_requested',
    // Được nhắc tên (@) trong một tài liệu — APM-CD2-f.
    'document.mentioned',
];
export const TelegramNotificationType = z.enum(TELEGRAM_NOTIFICATION_TYPES);
/**
 * Bật sẵn khi vừa liên kết — **không phải tất cả**, và đó là điểm chính.
 *
 * Những loại này là thứ đòi người nhận làm gì đó. Bật hết mọi loại là cách
 * nhanh nhất khiến người ta chặn bot trong tuần đầu, và một người đã chặn bot
 * thì không nhận được cả những tin thật sự gấp.
 */
export const TELEGRAM_DEFAULT_TYPES = [
    'task.assigned',
    'comment.mentioned',
    'announcement.sent',
    'membership_request.invited',
    // Người đăng ký đứng ngoài dự án tới khi PM chốt — việc của người nhận.
    'membership_request.applied',
    // Một dự án đứng chờ tới khi người duyệt mở trang chủ — cả đội người tạo
    // chưa bắt đầu được. Đúng loại "đòi người nhận làm gì đó".
    'project_approval.requested',
    'raid.risk_high',
    // Issue đã xử lý chờ người gửi xác nhận / bị mở lại chờ người xử lý — APM-ISX-c.
    'issue.resolved',
    'issue.reopened',
    // Có lời chờ mình trả lời / được giao một việc — APM-ISC-c. Trao đổi nội bộ
    // thì không bật sẵn: nó là chuyện của cả đội, bật hết là cách người ta chặn bot.
    'issue.replied',
    'issue.customer_replied',
    'issue.assigned',
    // Lượt gộp đứng chờ tới khi đủ mọi Admin + Director cả hai bên duyệt — APM-ORGMS-c.
    'org_restructure.updated',
    // Có KR tự do cần báo số — việc của chính người nhận, đúng một tin mỗi mốc
    // nhắc. Người dùng chốt 2026-10-01: bật cho mọi người đã liên kết (liên kết cũ
    // được bật bằng migration `apm_okr_report_due_telegram`).
    'okr.report_due',
    // Lỗi kiểm thử — APM-TEST2-b. Ba loại đòi người nhận làm gì đó (sửa / kiểm lại /
    // sửa tiếp); "đã đóng" chỉ là tin báo nên không bật sẵn.
    'bug.assigned',
    'bug.fixed',
    'bug.still_failing',
    // Được mời chốt sổ phương án — APM-DSHEET-i. Sổ đứng chờ tới khi người chốt chọn,
    // thường là BA / khách: đúng loại "đòi người nhận làm gì đó". Người dùng chốt
    // 2026-10-07: bật cho mọi người đã liên kết (migration `apm_dsheet_i_telegram_default`).
    'decision_sheet.invited',
    // Tài liệu chờ PM duyệt — người viết đứng chờ tới khi PM duyệt. Bật cho mọi người
    // đã liên kết (migration `apm_docrv_telegram_default`).
    'document.review_requested',
    // Việc đang làm sắp hết thời gian — việc của chính người nhận, một tin mỗi việc. Bật cho
    // mọi người đã liên kết (migration `apm_tlow_telegram_default`).
    'task.time_running_out',
    // Đầu ca chưa bấm giờ — việc của chính người nhận, một tin mỗi ca. Bật cho mọi người đã
    // liên kết (migration `apm_trem_c_telegram_default`); admin tổ chức chọn vai trò nhận nhắc.
    'timesheet.start_reminder',
    // Đơn nghỉ chờ quản lý duyệt, và đơn bị rút — người nhận là quản lý trực tiếp (không có ⇒
    // Giám đốc). Bật cho mọi người đã liên kết (migration `apm_trem_e_telegram_default`).
    'absence.requested',
    'absence.retracted',
    // Người xin vào tổ chức đứng ngoài cửa tới khi quản trị duyệt — APM-ORGJR-c. Người dùng chốt
    // 2026-10-22: bật cho mọi người đã liên kết (migration `apm_orgjr_c_telegram_default`); chỉ
    // quản trị tổ chức nhận.
    'org_join_request.created',
];
/**
 * Nhóm loại thông báo cho trang Cài đặt → Telegram — người dùng yêu cầu 2026-10-15.
 *
 * Bốn mươi lăm công tắc xếp phẳng buộc người ta đọc hết mới tìm được thứ mình cần.
 * Gom theo **việc người nhận đang làm** (việc của tôi, bấm giờ, issue, quản lý dự án…)
 * thì một nhân viên lướt qua nhóm của PM, và PM thấy ngay cả khối "Kế hoạch & rủi ro".
 *
 * ⚠️ Mỗi loại thuộc **đúng một** nhóm — `telegram.spec.ts` canh. Thêm loại mới mà quên
 * xếp nhóm thì nó **biến mất khỏi trang Cài đặt**, không gì báo lỗi.
 */
export const TELEGRAM_NOTIFICATION_GROUPS = [
    {
        key: 'work',
        types: [
            'task.assigned',
            'task.unassigned',
            'task.edited',
            'task.time_running_out',
            'comment.mentioned',
            'handover.suggested',
            'decision_sheet.invited',
        ],
    },
    {
        key: 'timesheet',
        types: [
            'timesheet.timer_overdue',
            'timesheet.session_capped',
            'timesheet.overtime_capped',
            'timesheet.day_capped',
            'timesheet.start_reminder',
        ],
    },
    {
        key: 'issue',
        types: [
            'issue.assigned',
            'issue.replied',
            'issue.customer_replied',
            'issue.internal_comment',
            'issue.state_changed',
            'issue.resolved',
            'issue.reopened',
            'issue.watcher_added',
        ],
    },
    { key: 'bug', types: ['bug.assigned', 'bug.fixed', 'bug.still_failing', 'bug.closed'] },
    {
        key: 'document',
        types: [
            'document.review_requested',
            'document.mentioned',
            'design_file.remote_version_detected',
        ],
    },
    {
        key: 'project',
        types: [
            'membership_request.invited',
            'membership_request.applied',
            'project_approval.requested',
            'project_approval.approved',
            'project_approval.rejected',
            'project_approval.decided_by_other',
            'project.member_left',
        ],
    },
    {
        key: 'planning',
        types: [
            'schedule.drift_detected',
            'schedule.impact_detected',
            'schedule.deadline_changed',
            'sprint.ending_soon',
            'sprint.last_day',
            'sprint.auto_closed',
            'raid.risk_high',
            'raid.signals_detected',
        ],
    },
    {
        key: 'org',
        types: [
            'absence.requested',
            'absence.retracted',
            'org_join_request.created',
            'announcement.sent',
            'okr.report_due',
            'org_restructure.updated',
            'ai_budget.soft_limit_reached',
        ],
    },
];
export const TelegramLinkStatus = z.object({
    /** Bot của bản cài đặt đã cấu hình chưa. Chưa ⇒ panel chỉ hiện một dòng giải thích. */
    botConfigured: z.boolean(),
    linked: z.boolean(),
    username: z.string().nullable(),
    firstName: z.string().nullable(),
    linkedAt: z.iso.datetime().nullable(),
    /** Có giá trị ⇒ người dùng đã chặn bot hoặc xoá cuộc trò chuyện. */
    blockedAt: z.iso.datetime().nullable(),
    types: z.array(TelegramNotificationType),
    /** Số phút tính từ 0h theo giờ địa phương. Cả hai `null` = không đặt giờ im lặng. */
    quietFrom: z.number().int().min(0).max(1439).nullable(),
    quietTo: z.number().int().min(0).max(1439).nullable(),
});
export const TelegramLinkTicket = z.object({
    /** `https://t.me/<bot>?start=<token>` — dựng sẵn ở server vì chỉ server biết `botUsername`. */
    deepLink: z.url(),
    expiresAt: z.iso.datetime(),
});
export const SetTelegramPrefsInput = z
    .object({
    types: z.array(TelegramNotificationType).max(TELEGRAM_NOTIFICATION_TYPES.length),
    quietFrom: z.number().int().min(0).max(1439).nullable(),
    quietTo: z.number().int().min(0).max(1439).nullable(),
})
    .refine((v) => (v.quietFrom === null) === (v.quietTo === null), {
    message: 'Giờ im lặng phải khai cả hai đầu, hoặc bỏ trống cả hai',
    path: ['quietTo'],
});
// ── Mini App (APM-TG-i) ─────────────────────────────────────────────────────
export const TelegramMiniAppLoginInput = z.object({
    /**
     * `window.Telegram.WebApp.initData` — nguyên văn, **không** parse ở client.
     *
     * Chữ ký được tính trên chính chuỗi này; tách rồi ghép lại ở client là cách
     * chắc chắn nhất để chữ ký không còn khớp, và triệu chứng là "sai chữ ký" cho
     * mọi lượt đăng nhập.
     */
    initData: z.string().min(1).max(8192),
});
//# sourceMappingURL=telegram.js.map